import { useEffect, useState, useCallback } from "react";
import { Lock, Download, Search, Trophy, LogOut, Users, Zap, CalendarClock, Package } from "lucide-react";
import { toast } from "sonner";
import { getAdminOverview, getAdminEntrants, getAdminOrders, adminEntrantsCsvUrl } from "../../lib/api";

const FULFILLMENT_STYLE = {
  submitted: "bg-green-500/15 text-green-400 border-green-500/40",
  draft: "bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/40",
  "mock-draft": "bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/40",
  awaiting_shipping: "bg-orange-500/10 text-orange-400 border-orange-500/40",
  unmapped: "bg-orange-500/10 text-orange-400 border-orange-500/40",
  failed: "bg-red-500/10 text-red-400 border-red-500/40",
};
const FULFILLMENT_LABEL = {
  submitted: "Submitted", draft: "Draft", "mock-draft": "Mock Draft",
  awaiting_shipping: "Awaiting Address", unmapped: "Unmapped", failed: "Failed",
};

const FulfillmentBadge = ({ status, error }) => {
  if (!status) return <span className="text-neutral-600 text-xs">—</span>;
  return (
    <span
      title={error || ""}
      data-testid="fulfillment-badge"
      className={`inline-block px-2 py-1 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${FULFILLMENT_STYLE[status] || "bg-white/5 text-neutral-400 border-white/10"}`}
    >
      {FULFILLMENT_LABEL[status] || status}
    </span>
  );
};

/** Secure admin dashboard — gated by the server-side ADMIN_TOKEN. The token
 *  is verified against /api/admin/overview before anything renders and is
 *  kept only in sessionStorage (cleared on tab close / sign out). */
export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [overview, setOverview] = useState(null);
  const [entrants, setEntrants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("entrants");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const loadEntrants = useCallback(async (t, s) => {
    const data = await getAdminEntrants(t, s || undefined);
    setEntrants(data.entrants);
  }, []);

  const login = async (t) => {
    setLoading(true);
    try {
      const ov = await getAdminOverview(t);
      setOverview(ov);
      await loadEntrants(t, "");
      const od = await getAdminOrders(t);
      setOrders(od.orders);
      sessionStorage.setItem("admin_token", t);
      setAuthed(true);
    } catch {
      toast.error("Invalid admin token");
      sessionStorage.removeItem("admin_token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && !authed) login(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authed) return;
    const id = setTimeout(() => {
      loadEntrants(token, search).catch(() => toast.error("Search failed"));
    }, 250);
    return () => clearTimeout(id);
  }, [search, authed, token, loadEntrants]);

  const signOut = () => {
    sessionStorage.removeItem("admin_token");
    setAuthed(false);
    setToken("");
    setOverview(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5" data-testid="admin-login">
        <div className="w-full max-w-sm bg-[#0c0c0c] border border-white/10 rounded-sm p-8">
          <div className="mx-auto h-12 w-12 rounded-full bg-[#CCFF00] flex items-center justify-center">
            <Lock className="h-5 w-5 text-black" strokeWidth={2.5} />
          </div>
          <h1 className="mt-5 text-center font-display font-black uppercase tracking-tighter text-2xl">
            Admin Access
          </h1>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login(token)}
            placeholder="Admin token"
            data-testid="admin-token-input"
            className="mt-6 w-full bg-[#111] border border-white/15 rounded-sm px-4 py-3 text-sm outline-none focus:border-[#CCFF00] transition-colors"
          />
          <button
            onClick={() => login(token)}
            disabled={loading || !token}
            data-testid="admin-login-button"
            className="mt-3 w-full bg-[#CCFF00] text-black py-3 rounded-sm font-black uppercase text-xs tracking-wider hover:bg-white transition-colors disabled:opacity-60"
          >
            {loading ? "Verifying…" : "Enter Dashboard"}
          </button>
        </div>
      </div>
    );
  }

  const w = overview?.winner;
  return (
    <div className="min-h-screen bg-[#050505] text-white px-5 md:px-10 py-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-5">
          <h1 className="font-display font-black uppercase tracking-tighter text-2xl">
            12 <span className="text-[#CCFF00]">O'CLOCK</span> — Admin
          </h1>
          <button
            onClick={signOut}
            data-testid="admin-signout-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {w && (
          <div className="mt-6 bg-[#CCFF00]/10 border border-[#CCFF00]/50 rounded-sm p-5 flex items-center gap-4" data-testid="winner-banner">
            <Trophy className="h-8 w-8 text-[#CCFF00]" />
            <div>
              <p className="font-black uppercase tracking-wide text-[#CCFF00]">Winner drawn</p>
              <p className="text-sm text-neutral-300">
                {w.name} · {w.email} · {w.entries} entries
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-sm p-5" data-testid="admin-stat-entrants">
            <Users className="h-4 w-4 text-[#CCFF00] mb-2" />
            <div className="font-display font-black text-3xl">{overview?.total_entrants ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Entrants</div>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-sm p-5" data-testid="admin-stat-entries">
            <Zap className="h-4 w-4 text-[#CCFF00] mb-2" />
            <div className="font-display font-black text-3xl">{overview?.total_entries ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total Entries</div>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-sm p-5" data-testid="admin-draw-date">
            <CalendarClock className="h-4 w-4 text-[#CCFF00] mb-2" />
            <div className="font-display font-black text-xl">
              {overview ? new Date(overview.draw_date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {overview?.drawn ? "Draw complete" : "Auto-draw at zero-hour"}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-2" data-testid="admin-tabs">
          <button
            onClick={() => setTab("entrants")}
            data-testid="tab-entrants"
            className={`px-5 py-2.5 rounded-sm font-black uppercase text-xs tracking-wider transition-colors ${tab === "entrants" ? "bg-[#CCFF00] text-black" : "bg-[#0c0c0c] border border-white/10 text-neutral-400 hover:text-white"}`}
          >
            <Users className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />Entrants
          </button>
          <button
            onClick={() => setTab("orders")}
            data-testid="tab-orders"
            className={`px-5 py-2.5 rounded-sm font-black uppercase text-xs tracking-wider transition-colors ${tab === "orders" ? "bg-[#CCFF00] text-black" : "bg-[#0c0c0c] border border-white/10 text-neutral-400 hover:text-white"}`}
          >
            <Package className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />Orders · Fulfillment
          </button>
        </div>

        {tab === "entrants" ? (
          <>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search entrants by email…"
              data-testid="entrant-search-input"
              className="w-full bg-[#0c0c0c] border border-white/15 rounded-sm pl-10 pr-4 py-3 text-sm outline-none focus:border-[#CCFF00] transition-colors"
            />
          </div>
          <a
            href={adminEntrantsCsvUrl(token)}
            download
            data-testid="export-csv-button"
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#CCFF00] text-black px-6 py-3 rounded-sm font-black uppercase text-xs tracking-wider hover:bg-white transition-colors"
          >
            <Download className="h-4 w-4" /> Export Full List
          </a>
        </div>

        <div className="mt-4 bg-[#0c0c0c] border border-white/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="entrant-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/10">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Entries</th>
                <th className="px-4 py-3">Orders</th>
                <th className="px-4 py-3">Spent</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Last Entry</th>
              </tr>
            </thead>
            <tbody>
              {entrants.map((e) => (
                <tr key={e.email} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="entrant-row">
                  <td className="px-4 py-3 text-neutral-200">{e.email}</td>
                  <td className="px-4 py-3 font-bold text-[#CCFF00]">{e.entries}</td>
                  <td className="px-4 py-3 text-neutral-400">{e.orders}</td>
                  <td className="px-4 py-3 text-neutral-400">${e.spent.toFixed(2)}</td>
                  <td className="px-4 py-3 text-neutral-400">{e.amoe ? "Free + Paid" : "Paid"}</td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">
                    {e.last_entry ? new Date(e.last_entry).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                  </td>
                </tr>
              ))}
              {entrants.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500" data-testid="entrant-empty">No entrants found</td></tr>
              )}
            </tbody>
          </table>
        </div>
          </>
        ) : (
        <div className="mt-6 bg-[#0c0c0c] border border-white/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/10">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Fulfillment</th>
                <th className="px-4 py-3">Printful ID</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="order-row">
                  <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">
                    {o.created_at ? new Date(o.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-4 py-3 text-neutral-200">{o.email}</td>
                  <td className="px-4 py-3 text-neutral-400 text-xs max-w-[280px] truncate" title={o.items_summary}>{o.items_summary || "—"}</td>
                  <td className="px-4 py-3 text-neutral-200">${(o.total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${o.payment_status === "paid" ? "text-green-400" : "text-neutral-500"}`}>
                      {o.payment_provider} · {o.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <FulfillmentBadge status={o.fulfillment_status} error={o.fulfillment_error} />
                  </td>
                  <td className="px-4 py-3 text-neutral-500 text-xs font-mono">{o.printful_order_id || "—"}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500" data-testid="orders-empty">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
